import { catchError, firstValueFrom, from, mergeMap, Observable, tap, throwError } from 'rxjs';
import {
    AnalyticsContextService,
    ApiUrlService,
    ApiVersionService,
    EnvDataService,
    IframeService,
    NotificationService,
    StorageKey,
    StorageService,
    WebBundleService,
} from '@lib/core';
import { I18nService } from '@lib/i18n';
import { EnvService } from '@lib/env';
import { OAuthSessionManager } from '@lib/auth';
import { Injectable, inject } from '@angular/core';
import { ESupportedLanguage, Logger } from '@lib/util';
import { renderStartupError } from './startup-error';

@Injectable({ providedIn: 'root' })
export class AppInitService {
    private static readonly WEB_BUNDLE_RELOAD_TIMEOUT_MS = 15_000;

    private readonly apiUrlService = inject(ApiUrlService);
    private readonly iframeService = inject(IframeService);
    private readonly apiVersionService = inject(ApiVersionService);
    private readonly envDataService = inject(EnvDataService);
    private readonly i18nService = inject(I18nService);
    private readonly storageService = inject(StorageService);
    private readonly notificationService = inject(NotificationService);
    private readonly sessionManager = inject(OAuthSessionManager);
    private readonly webBundleService = inject(WebBundleService);
    private readonly analyticsContext = inject(AnalyticsContextService);

    init(): Observable<void> {
        return this.runInitialization().pipe(
            catchError((error: unknown) => {
                // Angular never bootstraps when an APP_INITIALIZER rejects, so without this the
                // user only sees a blank page. See EPROC-11535.
                renderStartupError(error);

                return throwError(() => error);
            }),
        );
    }

    private runInitialization(): Observable<void> {
        if (this.isPublicRoute()) {
            return from(this.initLanguageOnly()) as Observable<void>;
        }

        return from(this.storageService.init()).pipe(
            tap(() => Logger.info('INIT', 'storage initialized')),
            mergeMap(() => this.initIframe()),
            mergeMap(() => this.initApiUrl()),
            tap(() => Logger.info('INIT', 'api url initialized')),
            // Before the auth initialization: it removes token mirrors the legacy bundle still reads.
            mergeMap(() => this.stayOnMatchingWebBundle()),
            mergeMap(() => this.sessionManager.initAuth()),
            tap(() => Logger.info('INIT', 'auth initialized')),
            mergeMap(() => this.checkApiUrl()),
            tap(() => Logger.info('INIT', 'api url checked')),
            tap(() => this.notificationService.spinner.setDefaultTimeout(EnvService.loadingOverlayDebounce)),
        ) as Observable<void>;
    }

    private async initApiUrl(): Promise<void> {
        if (!IframeService.inIframe()) {
            await this.apiUrlService.initApiUrl();
        }
    }

    private async checkApiUrl(): Promise<void> {
        const hasApiUrl: boolean = await this.apiUrlService.hasApiUrl();
        const apiUrlValid: boolean = await this.apiUrlService.apiUrlValid();
        if (hasApiUrl && apiUrlValid) {
            await firstValueFrom(this.apiVersionService.checkApiCompatibility());
            await firstValueFrom(this.envDataService.fetchEnvironmentVariables());
            // Needs the Matomo settings from the API; never throws and never waits for Matomo.
            this.analyticsContext.start();
        }
    }

    /**
     * Never resolves once the shell switched bundles: it reloads the WebView, and bootstrapping
     * this bundle meanwhile would already route and call the backend it does not serve. Should
     * the reload not come, the startup error replaces the endless splash screen.
     */
    private async stayOnMatchingWebBundle(): Promise<void> {
        if (!(await this.apiUrlService.hasApiUrl()) || !(await this.webBundleService.switchToMatchingBundle())) {
            return;
        }

        await new Promise<never>((_resolve, reject) =>
            setTimeout(
                () => reject(new Error('The app did not reload into the web bundle matching the server')),
                AppInitService.WEB_BUNDLE_RELOAD_TIMEOUT_MS,
            ),
        );
    }

    private async initIframe(): Promise<void> {
        if (IframeService.inIframe()) {
            await this.storageService.logout();
            await this.iframeService.init();
            Logger.info('INIT', 'iframe initialized');
        } else {
            await this.initLanguage();
            Logger.info('INIT', 'language initialized');
        }

        return Promise.resolve();
    }

    private async initLanguage(): Promise<void> {
        // A task deeplink carries the recipient's language. It has to win over the browser language here already:
        // the deeplink is opened without a session, so the initialization below would otherwise pick the browser
        // language and overwrite what the link asked for (EPROC-11750).
        const deeplinkLang: ESupportedLanguage | null = AppInitService.readDeeplinkLanguage();
        if (deeplinkLang) {
            await this.i18nService.setLanguageFromUserLang(deeplinkLang);

            return;
        }

        const storageLang: string | null = await this.storageService.getItem(StorageKey.LANGUAGE);
        const initLang: string = storageLang ? storageLang : ESupportedLanguage.en;
        const [hasOAuthSession, hasLegacyAccessToken] = await Promise.all([
            this.storageService.has(StorageKey.OAUTH_SESSION),
            this.storageService.has(StorageKey.ACCESS_TOKEN),
        ]);
        const isLoggedIn = hasOAuthSession || hasLegacyAccessToken;
        await this.i18nService.init(initLang, isLoggedIn);
    }

    private async initLanguageOnly(): Promise<void> {
        await this.i18nService.init(ESupportedLanguage.en, false);
    }

    private isPublicRoute(): boolean {
        return window.location.href.includes('password-reset');
    }

    /**
     * Returns the language requested by a task deeplink, or null when the URL carries none or an unsupported one.
     * An unsupported value is ignored on purpose so that the regular initialization keeps deciding.
     */
    private static readDeeplinkLanguage(): ESupportedLanguage | null {
        const requested: string | null = new URLSearchParams(window.location.search).get('language');
        if (!requested) {
            return null;
        }

        return (
            Object.values(ESupportedLanguage).find((supported) => supported === requested.toLowerCase()) ?? null
        );
    }
}
